/**
 * Resolved institution vocabulary for screens that must not hard-code "pupil" /
 * "class" when the seat is a university or vocational centre.
 *
 * Source of truth: GET /institutions/:id/experience/terminology — the same ladder
 * Configuration Studio edits. Soft-fails to neutral Student/Class/Teacher words
 * (never Pupil) so a permission miss cannot put school-age language on an HEI.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { institutionExperienceApi } from '@/api/institution-experience';
// The OPTIONAL context read, not useAppContext: that hook throws outside
// AppContextProvider, which took every vocabulary-aware panel down in any tree
// that renders without the provider (tests, isolated mounts). The context is only
// a fallback for the institution id here — callers normally pass it explicitly.
import { useOptionalAppContext } from '@/context/AppContextProvider';
import { useAsyncData } from './useAsyncData';
import type {
  CanonicalConcept,
  ResolvedTerm,
  ResolvedTerminology,
} from '@/types/institution-experience';

/** Floor when the resolver is unreachable — institution-neutral, not primary-school. */
const FLOOR: Partial<Record<CanonicalConcept, { singular: string; plural: string; short: string }>> = {
  LEARNER: { singular: 'Student', plural: 'Students', short: 'Student' },
  TEACHING_GROUP: { singular: 'Class group', plural: 'Class groups', short: 'Group' },
  TEACHER: { singular: 'Teacher', plural: 'Teaching staff', short: 'Teacher' },
  PROGRAMME_OF_STUDY: { singular: 'Programme', plural: 'Programmes', short: 'Programme' },
  ACADEMIC_UNIT: { singular: 'Department', plural: 'Departments', short: 'Dept' },
  ASSESSMENT: { singular: 'Assessment', plural: 'Assessments', short: 'Assessment' },
  ACADEMIC_PERIOD: { singular: 'Term', plural: 'Terms', short: 'Term' },
  ACADEMIC_YEAR: { singular: 'Academic year', plural: 'Academic years', short: 'Year' },
};

function floorTerm(key: CanonicalConcept, locale: string): ResolvedTerm {
  const base = FLOOR[key] ?? {
    singular: key.split('_').join(' ').toLowerCase(),
    plural: key.split('_').join(' ').toLowerCase() + 's',
    short: key,
  };
  return {
    ...base,
    canonicalKey: key,
    source: 'PLATFORM_DEFAULT',
    locale,
  };
}

function pick(
  terms: Partial<Record<CanonicalConcept, ResolvedTerm>>,
  key: CanonicalConcept,
  form: 'singular' | 'plural',
  locale: string,
  lower = false,
): string {
  const term = terms[key] ?? floorTerm(key, locale);
  const raw = form === 'plural' ? term.plural : term.singular;
  return lower ? raw.toLowerCase() : raw;
}

/**
 * i18n interpolation bag for overview / counsellor copy.
 * Title-case for labels; `*Lower` for mid-sentence English/French grammar.
 */
export type InstitutionVocabParams = {
  learner: string;
  learnerLower: string;
  learnerPlural: string;
  learnerPluralLower: string;
  teachingGroup: string;
  teachingGroupPlural: string;
  teachingGroupPluralLower: string;
  teacher: string;
  teacherPlural: string;
  programme: string;
  programmePlural: string;
  academicUnit: string;
  academicUnitPlural: string;
  academicUnitLower: string;
  academicUnitPluralLower: string;
};

export function vocabParamsFromTerms(
  terms: Partial<Record<CanonicalConcept, ResolvedTerm>>,
  locale: string,
): InstitutionVocabParams {
  return {
    learner: pick(terms, 'LEARNER', 'singular', locale),
    learnerLower: pick(terms, 'LEARNER', 'singular', locale, true),
    learnerPlural: pick(terms, 'LEARNER', 'plural', locale),
    learnerPluralLower: pick(terms, 'LEARNER', 'plural', locale, true),
    teachingGroup: pick(terms, 'TEACHING_GROUP', 'singular', locale),
    teachingGroupPlural: pick(terms, 'TEACHING_GROUP', 'plural', locale),
    teachingGroupPluralLower: pick(terms, 'TEACHING_GROUP', 'plural', locale, true),
    teacher: pick(terms, 'TEACHER', 'singular', locale),
    teacherPlural: pick(terms, 'TEACHER', 'plural', locale),
    programme: pick(terms, 'PROGRAMME_OF_STUDY', 'singular', locale),
    programmePlural: pick(terms, 'PROGRAMME_OF_STUDY', 'plural', locale),
    academicUnit: pick(terms, 'ACADEMIC_UNIT', 'singular', locale),
    academicUnitPlural: pick(terms, 'ACADEMIC_UNIT', 'plural', locale),
    academicUnitLower: pick(terms, 'ACADEMIC_UNIT', 'singular', locale, true),
    academicUnitPluralLower: pick(terms, 'ACADEMIC_UNIT', 'plural', locale, true),
  };
}

export function useInstitutionTerms(institutionId?: string): {
  vocab: InstitutionVocabParams;
  terms: Partial<Record<CanonicalConcept, ResolvedTerm>>;
  ready: boolean;
} {
  const { i18n } = useTranslation();
  const appContext = useOptionalAppContext();
  const id = institutionId ?? appContext?.activeContext?.institution?.id;
  const locale = i18n.language?.startsWith('fr') ? 'fr' : 'en';

  const { state } = useAsyncData<ResolvedTerminology>(
    `institution-terms-${id ?? 'none'}-${locale}`,
    () => institutionExperienceApi.terminology.resolve(id!, { locale }),
    Boolean(id),
  );

  return useMemo(() => {
    const terms =
      state.status === 'ready' ? state.data.terms : ({} as Partial<Record<CanonicalConcept, ResolvedTerm>>);
    return {
      vocab: vocabParamsFromTerms(terms, locale),
      terms,
      ready: state.status === 'ready',
    };
  }, [state, locale]);
}
